import { TWEEN } from 'three/examples/jsm/libs/tween.module.min';
import { ComponentInteractionType, SceneComponent } from '@/_models/virtualStaging/Scene.component';
import { LoadingState } from '@/_models/virtualStaging/VSObject';
import { getCurveLength } from '@/_models/virtualStaging/PathFinderLine/utils/getCurveLength';
import { calculateSubsegments } from '@/_models/virtualStaging/PathFinderLine/utils/calculateSubSegments';
import { stepsInSameTeleportGroup } from '@/_models/virtualStaging/PathFinderLine/utils/utils';

export class PathFinderLineComponent extends SceneComponent {
  public THREE: any;

  public root: any = null;

  public pivot: any = null;

  public line: any;

  public texture: any;

  public sphereArr: any = [];

  public animationId: any = null;

  private frustum: any;

  private leftHelperSphere: any;

  private rightHelperSphere: any;

  private leftHelperSphereWorld: any;

  private rightHelperSphereWorld: any;

  private pathSphereHelper: any;

  private cameraWorld: any;

  private cameraWorldQuaternion: any;

  private cameraViewProjectionMatrix: any;

  public isCameraFocused: any;

  public cameraDirectionVector: any;

  public horizonVector: any;

  public calculatedCameraDirectionVector: any;

  inputs: any = {
    camera: null,
    path: [],
    visible: true,
    color: 'white',
    arrowRef: null,
    teleportPoints: [],
    onDistanceChanged: null,
  };

  outputs: any = {
    loadingState: LoadingState.Idle,
  };

  events = {
    [ComponentInteractionType.CLICK]: false,
    [ComponentInteractionType.HOVER]: false,
    [ComponentInteractionType.DRAG]: false,
  };

  declare context: any;

  onInit() {
    const THREE = this.context.three;
    this.frustum = new THREE.Frustum();
    this.cameraViewProjectionMatrix = new THREE.Matrix4();
    this.cameraDirectionVector = new THREE.Vector3();
    this.horizonVector = new THREE.Vector3();
    this.calculatedCameraDirectionVector = new THREE.Vector3();
    this.root = new THREE.Object3D();
    this.root.visible = this.inputs.visible;
    this.pivot = new THREE.Object3D();
    this.root.add(this.pivot);
    this.outputs.objectRoot = this.root;
    this.createHelperSpheres();

    if (this.inputs.path.length) {
      this.createLineFromPath(this.inputs.path);
    }
  }

  createHelperSpheres() {
    const THREE = this.context.three;
    const geometry = new THREE.SphereGeometry(0.1, 32, 16);
    const material = new THREE.MeshBasicMaterial({ color: 0xffff00 });
    this.leftHelperSphere = new THREE.Mesh(geometry, material);
    this.rightHelperSphere = new THREE.Mesh(geometry, material);

    this.leftHelperSphere.scale.setScalar(0);
    this.rightHelperSphere.scale.setScalar(0);

    this.leftHelperSphere.position.x = -0.75;
    this.leftHelperSphere.position.z = -1;
    this.rightHelperSphere.position.x = 0.75;
    this.rightHelperSphere.position.z = -1;
    this.inputs.camera.add(this.leftHelperSphere);
    this.inputs.camera.add(this.rightHelperSphere);

    this.pathSphereHelper = new THREE.Vector3();
    this.leftHelperSphereWorld = new THREE.Vector3();
    this.rightHelperSphereWorld = new THREE.Vector3();
    this.cameraWorld = new THREE.Vector3();
    this.cameraWorldQuaternion = new THREE.Quaternion();
  }

  createLineFromPath(matterPath) {
    const THREE = this.context.three;
    let fullDistance = 0;

    for (let i = 0; i < matterPath.length; i++) {
      if (matterPath[i] && matterPath[i + 1]) {
        if (this.inputs.teleportPoints.length) {
          const skipIteration = stepsInSameTeleportGroup(matterPath[i], matterPath[i + 1], this.inputs.teleportPoints);
          // eslint-disable-next-line no-continue
          if (skipIteration) continue;
        }
        const start = new THREE.Vector3(matterPath[i].data.position.x, matterPath[i].data.position.y - 1.15, matterPath[i].data.position.z);
        const end = new THREE.Vector3(matterPath[i + 1].data.position.x, matterPath[i + 1].data.position.y - 1.15, matterPath[i + 1].data.position.z);
        const path = new THREE.CatmullRomCurve3([start, end]);

        const distance = getCurveLength(path);
        fullDistance += distance;
        const { subsegments } = calculateSubsegments(distance, 0.4);
        this.createSubSegments(subsegments, path, i === matterPath.length - 2);
      }
    }

    this.animateLine();
    this.outputs.loadingState = LoadingState.Loaded;
    this.emitDistanceEvent(fullDistance);
  }

  createSubSegments(count, path, isLastSegment) {
    const THREE = this.context.three;
    const textureLoader = new THREE.TextureLoader();
    this.texture = textureLoader.load('/images/path-sphere.png');
    const planeGeometry = new THREE.PlaneGeometry(0.13, 0.13);
    const sphereColor = new THREE.Color(0x39b6fe);

    for (let i = 0; i < count; i++) {
      const start = path.getPointAt(i / count);
      const end = path.getPointAt((i + 1) / count);
      const curve = new THREE.CatmullRomCurve3([start, end]);
      const startPoint = curve.getPointAt(0);
      const endPoint = curve.getPointAt(1);

      const planeMaterial = new THREE.MeshBasicMaterial({ color: sphereColor, map: this.texture, side: THREE.DoubleSide, transparent: true });
      planeMaterial.depthWrite = false;
      // Create a sphere at the start point
      const startSphere = new THREE.Mesh(planeGeometry, planeMaterial);

      startSphere.position.copy(startPoint);
      this.pivot.add(startSphere);
      this.sphereArr.push(startSphere);

      // Create a sphere at the end point of the last segment
      if (i === count - 1 && isLastSegment) {
        const planeMaterialCopy = new THREE.MeshBasicMaterial({ color: sphereColor, map: this.texture, side: THREE.DoubleSide, transparent: true });
        const endSphere = new THREE.Mesh(planeGeometry, planeMaterialCopy);
        endSphere.position.copy(endPoint);
        this.pivot.add(endSphere);
        this.sphereArr.push(endSphere);
      }
    }
  }

  animateLine() {
    const THREE = this.context.three;
    let currentIndex = 0;
    const animate = () => {
      if (!this.sphereArr || this.sphereArr.length === 0) {
        clearTimeout(this.animationId);
        return;
      }

      if (!this.sphereArr[currentIndex]) return;

      this.sphereArr.forEach((mesh, index) => {
        const isBlueSegment = index === currentIndex;

        if (isBlueSegment) {
          const startColor = new THREE.Color(0x39b6fe);
          const endColor = new THREE.Color(0xffffff);
          const fadeOutTween = new TWEEN.Tween(mesh.material.color).to(startColor, 35).easing(TWEEN.Easing.Quadratic.In).delay(10); // transition duration
          const fadeInTween = new TWEEN.Tween(mesh.material.color)
            .to(endColor, 35)
            .easing(TWEEN.Easing.Quadratic.Out)
            .onComplete(() => {
              fadeOutTween.start();
            });
          fadeInTween.start();
        }
      });

      currentIndex = (currentIndex + 1) % this.sphereArr.length;
      if (currentIndex === 0) this.animationId = setTimeout(animate, 1000);
      else this.animationId = setTimeout(animate, 80);
    };

    animate();
  }

  stopAnimation() {
    clearTimeout(this.animationId);
    this.animationId = null;
  }

  setVisible(visible) {
    this.inputs.visible = visible;
  }

  reset() {
    this.stopAnimation();
    if (this.sphereArr.length)
      this.sphereArr.forEach((sphere) => {
        sphere.removeFromParent();
      });
    this.sphereArr = [];
  }

  onInputsUpdated(previousInputs: this['inputs']) {
    this.reset();
    if (this.inputs.path.length > 1) {
      this.createLineFromPath(this.inputs.path);
    } else {
      this.emitDistanceEvent(0);
    }
  }

  getCameraAngle() {
    const arrowElement = this.inputs.arrowRef.current;

    this.inputs.camera.getWorldPosition(this.pathSphereHelper);

    if (this.sphereArr.length >= 5) {
      this.pathSphereHelper.x = this.sphereArr[4].position.x;
      this.pathSphereHelper.z = this.sphereArr[4].position.z;
    } else {
      this.pathSphereHelper.x = this.sphereArr[this.sphereArr.length - 1].position.x;
      this.pathSphereHelper.z = this.sphereArr[this.sphereArr.length - 1].position.z;
    }

    this.inputs.camera.getWorldPosition(this.cameraWorld);

    this.cameraDirectionVector.subVectors(this.pathSphereHelper, this.cameraWorld);
    this.cameraDirectionVector.project(this.inputs.camera);

    if (!this.isCameraFocused && this.leftHelperSphere) {
      const targetPathSphere = this.sphereArr.length >= 5 ? this.sphereArr[4] : this.sphereArr[this.sphereArr.length - 1];

      this.leftHelperSphere.getWorldPosition(this.leftHelperSphereWorld);
      this.rightHelperSphere.getWorldPosition(this.rightHelperSphereWorld);
      const arrowDirection =
        this.leftHelperSphereWorld.distanceTo(targetPathSphere.position) > this.rightHelperSphereWorld.distanceTo(targetPathSphere.position) ? 'right' : 'left';

      const left = arrowDirection === 'left' ? 40 : window.innerWidth - 40;
      const sign = arrowDirection === 'left' ? 1 : -1;

      this.horizonVector.set(0, this.inputs.camera.getWorldPosition(this.cameraWorld).y, 0).normalize();
      this.calculatedCameraDirectionVector.set(0, 0, -1).applyQuaternion(this.inputs.camera.getWorldQuaternion(this.cameraWorldQuaternion)).normalize();
      const cosAngle = this.calculatedCameraDirectionVector.y;

      let top;
      if (cosAngle < 0) {
        // eslint-disable-next-line no-restricted-properties
        top = (Math.pow(cosAngle, 4) * 0.25 + 0.5) * window.innerHeight;
      } else {
        // eslint-disable-next-line no-restricted-properties
        top = (Math.pow(cosAngle, 4) * -0.25 + 0.5) * window.innerHeight;
      }

      arrowElement.style.left = `${left}px`;
      arrowElement.style.top = `${top}px`;
      arrowElement.style.opacity = 1;
      arrowElement.style.transform = `rotate(${(1 - top / (window.innerHeight * 0.5)) * 0.25 * -sign + (sign === -1 ? 0 : 0.5)}turn)`;
    } else arrowElement.style.opacity = 0;
  }

  onTick() {
    this.inputs.camera.matrixWorldInverse.copy(this.inputs.camera.matrixWorld).invert();
    this.cameraViewProjectionMatrix.multiplyMatrices(this.inputs.camera.projectionMatrix, this.inputs.camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.cameraViewProjectionMatrix);
    if (this.sphereArr.length) {
      this.sphereArr.forEach((sphere) => {
        const { x, y, z } = this.inputs.camera.parent.position;
        sphere.lookAt(x, y, z);
      });

      this.isCameraFocused = false;
      this.sphereArr.forEach((sphere) => {
        if (this.frustum.intersectsObject(sphere)) this.isCameraFocused = true;
      });
      if (this.inputs.arrowRef?.current) this.getCameraAngle();
    } else if (this.inputs.arrowRef?.current) {
      const arrowElement = this.inputs.arrowRef.current;
      arrowElement.style.opacity = 0;
    }
  }

  onDestroy() {}

  private emitDistanceEvent(distance: number) {
    if (this.inputs.onDistanceChanged) {
      this.inputs.onDistanceChanged(distance % 1 === 0 ? distance.toString() : distance.toFixed(1));
    }
  }
}

export const pathFinderLineComponentType = 'mp.pathFinderLineComponent';
export const makePathFinderLineComponent = (): any => new PathFinderLineComponent();
